import axios from "axios";

import { defineStore } from "@/lib/tellscale/pinia.ts";

import type { BasicColorSchema, UseColorModeReturn } from '@vueuse/core';

import type { IBaseStore } from "@/lib/tellscale/pinia.ts";

import { datadogRum } from "@datadog/browser-rum";

import { useRecommendationPortal } from "@/stores/portal";
import { useAuth } from "@/stores/auth";
import { useApi } from "@/stores/api";

import type { AppConfigResponse, AppEnvironment }  from "@/stores/app";

/**
 * Main application store managing global app state and initialization
 */
export const useApp = defineStore('app', () => {
    const appEnvironment: AppEnvironment = import.meta.env.MODE as AppEnvironment;

    const { setLocale, locale, supportedLocales } = useI18n();

    const recommendationPortal = useRecommendationPortal();
    const auth = useAuth();
    const api = useApi();

    const colorMode = ref<UseColorModeReturn>(useColorMode());

    const isDarkMode = computed<boolean>(() => colorMode.value === 'dark');
    const isLightMode = computed<boolean>(() => colorMode.value === 'light');

    const isMobile = useMediaQuery("(max-width: 768px)");

    const disabledFeatures = ref<string[]>([]);

    /**
     * Check if a feature is enabled
     * Mirrors backend `ApplicationService::isFeatureEnabled` and main frontend `appStore.isFeatureEnabled`
     *
     * @param {string} feature - Feature key (e.g. a widget-key like 'activities')
     * @returns {boolean} True when the feature is NOT in the disabled list
     */
    const isFeatureEnabled = (feature: string): boolean => {
        return !disabledFeatures.value.includes(feature);
    };

    /**
     * Initialize the application by setting up Datadog, Axios, and handling authentication flow
     */
    const init = async (): Promise<void> => {
        console.info(`[App] Running in ${appEnvironment}`);

        reset();

        initDatadog();

        await api.initAxios(appEnvironment);

        await loadConfig();

        await boot();
    }

    /**
     * Load the public app config (disabled features etc.) from the backend
     * Driven by the TS_DISABLED_FEATURES env variable on service-tellscale-public
     */
    const loadConfig = async (): Promise<void> => {
        try {
            const { data } = await axios.get<AppConfigResponse>('/api/app/config');

            disabledFeatures.value = data.data.disabledFeatures ?? [];

            console.info('[App] Config loaded', { disabledFeatures: disabledFeatures.value });
        }
        catch(error) {
            console.error('[App] Failed to load app config', error);

            disabledFeatures.value = [];
        }
    }

    /**
     * Initialize Datadog RUM for application monitoring (skipped in local environment)
     */
    const initDatadog = (): void => {
        if(import.meta.env.VITE_DATADOG_ENABLED !== 'true' || appEnvironment === 'development') {
            console.info("[App] Skipping Datadog initialization: Datadog is disabled or app is running in development");
            return;
        }

        console.info("[App] Initializing Datadog RUM");

        datadogRum.init({
            applicationId: 'e90ee175-58de-437d-8a69-08138e82011b',
            clientToken: 'pubf744fa9fa5052bf830b212e065a79542',
            site: 'datadoghq.eu',
            service: 'tellscale-frontend-promoter',
            env: import.meta.env.MODE,
            version: import.meta.env.VITE_APP_VERSION || 'dev',
            sessionSampleRate: 100,
            sessionReplaySampleRate: 100,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: 'mask-user-input',
            allowedTracingUrls: [/https:\/\/api\..*tellscale\.de/]
        });
    }

    /**
     * Boot the application by resetting stores, authenticating user, and loading portal data
     */
    const boot = async (): Promise<void> => {
        console.info("[App] Booting application");

        await auth.authenticate();

        if(!auth.isAuthenticated) {
            await recommendationPortal.loadUsingIdentifiers();
        }
        else {
            await recommendationPortal.loadUsingAuthentication();
        }
    }

    /**
     * Set the application color mode (light/dark/auto)
     *
     * @param {BasicColorSchema} mode - Color mode to set
     */
    const setColorMode = (mode: BasicColorSchema): void => {
        colorMode.value = mode;
    }

    /**
     * Toggle between light and dark color modes
     */
    const toggleColorMode = (): void => {
        colorMode.value = isDarkMode.value ? 'light' : 'dark';
    }

    /**
     * Reset all Pinia stores to their initial state
     *
     * AUTO-DISCOVERY: Automatically finds and resets all registered stores
     * by iterating through Pinia's internal store registry
     */
    const reset = (): void => {
        const pinia = getActivePinia();

        if(!pinia) {
            console.warn('No active Pinia instance found while performing reset.');
            return;
        }

        const stores = (pinia as any)._s as Map<string, any>;

        stores.forEach((store: any, storeId: string) => {
            if (storeId === 'app') return;

            const baseStore = store as IBaseStore;

            if(typeof baseStore.reset === 'function') {
                console.info(`[App] Resetting store: ${storeId}`);
                baseStore.reset();
            }
        });
    }

    return {
        appEnvironment, isLightMode, isDarkMode, locale, supportedLocales, setLocale, colorMode, isMobile,
        disabledFeatures, isFeatureEnabled,
        init, loadConfig, setColorMode, toggleColorMode, reset,
    };
});
